import { useCallback, useMemo, useState } from "react";
import { Badge, Button, IconButton } from "@radix-ui/themes";
import { PlusIcon, DrawingPinIcon, Cross2Icon } from "@radix-ui/react-icons";
import { SelectOption } from "@/components/shared/select";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import {
  isReasonForNegativeScore,
  ReasonForNegativeScore,
  reasonsForNegativeScore,
} from "@/v2/entities/qa-score/QaScore.model";
import { reasonsForNegativeScoreFormatted } from "@/v2/entities/qa-score/reasonsForNegativeScoreFormatted";
import styles from "./QaScore.module.scss";

interface QaScoreReasonsProps {
  reasons?: ReasonForNegativeScore[];
  value?: ReasonForNegativeScore[];
  updateScoreReasons: (newValue: ReasonForNegativeScore[]) => Promise<void>;
  viewOnly?: boolean;
}
export function QaScoreReasons({
  reasons = [...reasonsForNegativeScore],
  value,
  updateScoreReasons,
  viewOnly = false,
}: QaScoreReasonsProps) {
  const isEmpty = value == null || value.length === 0;

  const [addingReason, setAddingReason] = useState(false);
  const [reasonInRemoving, setReasonInRemoving] = useState<string>();

  const options = useMemo<SelectOption[]>(() => {
    if (isEmpty) {
      return reasons.map((value) => ({
        value,
        label: reasonsForNegativeScoreFormatted[value],
      }));
    }

    const valueAsMap = new Set(value);
    return reasons
      .filter((reason) => !valueAsMap.has(reason))
      .map((value) => ({
        value,
        label: reasonsForNegativeScoreFormatted[value],
      }));
  }, [isEmpty, value, reasons]);

  const addReason = useCallback(
    async (reason: string) => {
      if (!isReasonForNegativeScore(reason)) {
        return;
      }

      setAddingReason(true);
      await updateScoreReasons([...(value ?? []), reason]);
      setAddingReason(false);
    },
    [value, updateScoreReasons]
  );

  const removeReason = useCallback(
    async (reason: string) => {
      if (!isReasonForNegativeScore(reason)) {
        return;
      }

      setReasonInRemoving(reason);
      await updateScoreReasons((value ?? []).filter((r) => r !== reason));
      setReasonInRemoving(undefined);
    },
    [value, updateScoreReasons]
  );

  return (
    <div className={styles.QaScoreReason}>
      {value?.map((reason) => (
        <Badge color={"gray"} key={reason} size={"2"}>
          {reasonsForNegativeScoreFormatted[reason]}

          <IconButton
            color="gray"
            variant="ghost"
            highContrast
            size="1"
            loading={reasonInRemoving === reason}
            onClick={() => removeReason(reason)}
            disabled={viewOnly}
          >
            <Cross2Icon />
          </IconButton>
        </Badge>
      ))}

      {!viewOnly && (
        <DropdownMenu
          items={options.map((option) => ({
            label: option.label,
            onClick: () => addReason(option.value),
          }))}
        >
          <DropdownMenu.Trigger>
            {isEmpty ? (
              <Button
                color="gray"
                variant="ghost"
                highContrast
                size="1"
                loading={addingReason}
                className={styles.ReasonMenuButton}
                disabled={viewOnly}
              >
                <DrawingPinIcon />
                Add reason
              </Button>
            ) : (
              <IconButton
                color="gray"
                variant="ghost"
                highContrast
                size="1"
                loading={addingReason}
                className={styles.ReasonMenuButton}
                disabled={viewOnly}
              >
                <PlusIcon />
              </IconButton>
            )}
          </DropdownMenu.Trigger>
        </DropdownMenu>
      )}
    </div>
  );
}
